import { isNil } from 'ramda';
import { useRef, useEffect, useMemo } from 'react';
import { zIndex } from '~/theme/System/tokens';
import type { Reporter } from '~/contexts/ErrorReporterContext';
import useErrorReporter from '~/hooks/useErrorReporter';
export const MODAL_PORTAL_CLASS_NAME = 'react-modal-portal-class';

export const MIN_PORTAL_Z_INDEX = zIndex.portal;

/**
 * Gets the highest z-index from all existing portals in the DOM.
 */
const getMaxPortalZIndex = (reporter: Reporter): number => {
  const portals = document.querySelectorAll(`.${MODAL_PORTAL_CLASS_NAME}`);

  let maxZIndex = MIN_PORTAL_Z_INDEX - 1;

  portals.forEach(portal => {
    // Check if element is still connected to DOM
    if (!portal.isConnected) {
      return;
    }

    try {
      const computedStyle = window.getComputedStyle(portal);
      const zIndex = parseInt(computedStyle.zIndex, 10);

      if (!isNaN(zIndex) && zIndex > maxZIndex) {
        maxZIndex = zIndex;
      }
    } catch (error) {
      reporter.captureException(error, 'warning');
    }
  });

  return maxZIndex;
};

/**
 * Creates DOM element to be used as React root.
 */
const createElement = (id: string): HTMLElement => {
  const rootContainer = document.createElement('div');
  rootContainer.setAttribute('id', id);
  return rootContainer;
};

/**
 * Appends element as last child of body.
 */
const addRootElement = (rootElem: Element) => {
  if (document.body.lastElementChild)
    document.body.insertBefore(
      rootElem,
      document.body.lastElementChild.nextElementSibling,
    );
};

/**
 * Hook to create a React Portal.
 * Automatically handles creating and tearing-down the root elements
 * so there is no need to ensure the parent target already
 * exists.
 */
const usePortal = (id: string = 'modal-portal'): HTMLElement => {
  const reporter = useErrorReporter();
  const rootElemRef = useRef<HTMLElement | null>(null);

  const getRootElem = useMemo(
    () => () => {
      if (isNil(rootElemRef.current)) {
        const inner = createElement(`${id}-inner`);
        inner.style.pointerEvents = 'auto';
        rootElemRef.current = inner;
      }

      return rootElemRef.current;
    },
    [id],
  );

  useEffect(
    function setupElement() {
      // Look for existing target dom element to append to
      const existingParent = document.querySelector(`#${id}-root`);

      // if the parent already exists, remove and recreate it
      // so we can ensure its added to the end of the document
      if (existingParent) {
        existingParent.remove();
      }

      // Create fresh parent element
      const parentElem = createElement(`${id}-root`);
      parentElem.classList.add(MODAL_PORTAL_CLASS_NAME);
      parentElem.style.position = 'fixed';
      parentElem.style.top = '0';
      parentElem.style.left = '0';
      parentElem.style.right = '0';
      parentElem.style.bottom = '0';
      parentElem.style.pointerEvents = 'none';

      // Calculate z-index based on existing portals
      const maxZIndex = getMaxPortalZIndex(reporter);
      const newZIndex = Math.max(maxZIndex + 1, MIN_PORTAL_Z_INDEX);
      parentElem.style.zIndex = String(newZIndex);

      // Add parent to end of document
      addRootElement(parentElem);

      const rootEl = getRootElem();
      // Add the detached element to the parent
      parentElem.appendChild(rootEl);

      return function removeElement() {
        const parent = document.querySelector(`#${id}-root`);

        rootEl?.remove();
        parent?.remove();
      };
    },
    [getRootElem, id, reporter],
  );

  return getRootElem();
};

export default usePortal;
